import { useState, useEffect } from 'react';
import api from './api';
import './AccountManagement.css';

const STATUS_LABELS = { 0: '대기', 1: '승인', 2: '운항중', 3: '완료', 4: '취소' };
const STATUS_CSS    = { 0: 'fp-s0', 1: 'fp-s1', 2: 'fp-s2', 3: 'fp-s3', 4: 'fp-s4' };

const fmtDate   = v => v ? v.replace('T', ' ') : '-';
const toApiDt   = v => v ? v + ':00' : null;
const toInputDt = v => v ? v.slice(0, 16) : '';

const EMPTY_CREATE = {
  flightPlanId: '', aircraftUid: '', depVertiportUid: '', arrVertiportUid: '',
  airwayUid: '', cruiseAlt: '', scheduledDepTime: '', scheduledArrTime: '',
};

function initEditForm(fp) {
  return {
    airwayUid:        fp.airwayUid ?? '',
    cruiseAlt:        fp.cruiseAlt ?? '',
    status:           fp.status,
    scheduledDepTime: toInputDt(fp.scheduledDepTime),
    scheduledArrTime: toInputDt(fp.scheduledArrTime),
    actualDepTime:    toInputDt(fp.actualDepTime),
    actualArrTime:    toInputDt(fp.actualArrTime),
  };
}

export default function FlightPlanManagement() {
  const [flightPlans, setFlightPlans] = useState([]);
  const [aircrafts,   setAircrafts]   = useState([]);
  const [vertiports,  setVertiports]  = useState([]);
  const [airways,     setAirways]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null);
  const [mode,        setMode]        = useState('view');
  const [editForm,    setEditForm]    = useState(initEditForm({ airwayUid: '', cruiseAlt: '', status: 0, scheduledDepTime: '', scheduledArrTime: '', actualDepTime: '', actualArrTime: '' }));
  const [createForm,  setCreateForm]  = useState(EMPTY_CREATE);
  const [saving,      setSaving]      = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [msg,         setMsg]         = useState({ text: '', type: '' });

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const [fpRes, acRes, vpRes, awRes] = await Promise.all([
        api.get('/api/flight-plan/list'),
        api.get('/api/aircraft/list'),
        api.get('/api/vertiport/list'),
        api.get('/api/airway/list'),
      ]);
      setFlightPlans(fpRes.data);
      setAircrafts(acRes.data);
      setVertiports(vpRes.data);
      setAirways(awRes.data);
    } catch {
      setMsg({ text: '데이터를 불러오지 못했습니다.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function refreshPlans() {
    try {
      const res = await api.get('/api/flight-plan/list');
      setFlightPlans(res.data);
      return res.data;
    } catch {
      setMsg({ text: '목록 갱신에 실패했습니다.', type: 'error' });
      return null;
    }
  }

  function findAc(uid) { return aircrafts.find(a => String(a.aircraftUid) === String(uid)); }
  function findVp(uid) { return vertiports.find(v => String(v.vertiportUid) === String(uid)); }

  function selectPlan(fp) {
    setSelected(fp);
    setEditForm(initEditForm(fp));
    setMode('view');
    setMsg({ text: '', type: '' });
    setConfirmDelete(false);
  }

  function openCreate() {
    setSelected(null);
    setCreateForm(EMPTY_CREATE);
    setMode('create');
    setMsg({ text: '', type: '' });
    setConfirmDelete(false);
  }

  function setE(k, v) { setEditForm(f => ({ ...f, [k]: v })); }
  function setC(k, v) { setCreateForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    setMsg({ text: '', type: '' });
    try {
      const body = {
        airwayUid:        editForm.airwayUid !== '' ? Number(editForm.airwayUid) : null,
        cruiseAlt:        editForm.cruiseAlt !== '' ? Number(editForm.cruiseAlt) : null,
        status:           Number(editForm.status),
        scheduledDepTime: toApiDt(editForm.scheduledDepTime),
        scheduledArrTime: toApiDt(editForm.scheduledArrTime),
        actualDepTime:    toApiDt(editForm.actualDepTime),
        actualArrTime:    toApiDt(editForm.actualArrTime),
      };
      const res = await api.put(`/api/flight-plan/${selected.flightPlanUid}`, body);
      if (res.data.success !== false) {
        setMsg({ text: '수정되었습니다.', type: 'success' });
        const list = await refreshPlans();
        if (list) {
          const refreshed = list.find(fp => fp.flightPlanUid === selected.flightPlanUid);
          if (refreshed) { setSelected(refreshed); setEditForm(initEditForm(refreshed)); }
        }
      } else {
        setMsg({ text: res.data.message || '수정에 실패했습니다.', type: 'error' });
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    if (!selected || selected.status !== 0) return;
    setSaving(true);
    setMsg({ text: '', type: '' });
    try {
      const res = await api.post(`/api/flight-plan/${selected.flightPlanUid}/approve`);
      if (res.data.success !== false) {
        setMsg({ text: '승인되었습니다.', type: 'success' });
        const list = await refreshPlans();
        if (list) {
          const refreshed = list.find(fp => fp.flightPlanUid === selected.flightPlanUid);
          if (refreshed) { setSelected(refreshed); setEditForm(initEditForm(refreshed)); }
        }
      } else {
        setMsg({ text: res.data.message || '승인에 실패했습니다.', type: 'error' });
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleCreate() {
    const { flightPlanId, aircraftUid, depVertiportUid, arrVertiportUid, airwayUid, scheduledDepTime, scheduledArrTime } = createForm;
    if (!flightPlanId.trim() || !aircraftUid || !depVertiportUid || !arrVertiportUid || !airwayUid || !scheduledDepTime || !scheduledArrTime) {
      setMsg({ text: '필수 항목을 모두 입력하세요.', type: 'error' });
      return;
    }
    if (depVertiportUid === arrVertiportUid) {
      setMsg({ text: '출발 버티포트와 도착 버티포트가 동일합니다.', type: 'error' });
      return;
    }
    setSaving(true);
    setMsg({ text: '', type: '' });
    try {
      const res = await api.post('/api/flight-plan/create', {
        flightPlanId:     flightPlanId.trim(),
        aircraftUid:      Number(aircraftUid),
        depVertiportUid:  Number(depVertiportUid),
        arrVertiportUid:  Number(arrVertiportUid),
        airwayUid:        Number(airwayUid),
        cruiseAlt:        createForm.cruiseAlt !== '' ? Number(createForm.cruiseAlt) : null,
        scheduledDepTime: toApiDt(scheduledDepTime),
        scheduledArrTime: toApiDt(scheduledArrTime),
      });
      if (res.data.success) {
        const list = await refreshPlans();
        const newPlan = list?.find(fp => fp.flightPlanUid === res.data.flightPlanUid);
        if (newPlan) selectPlan(newPlan);
        else setMode('view');
        setCreateForm(EMPTY_CREATE);
        setMsg({ text: '비행 계획이 등록되었습니다.', type: 'success' });
      } else {
        setMsg({ text: res.data.message || '등록에 실패했습니다.', type: 'error' });
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!selected) return;
    setSaving(true);
    setMsg({ text: '', type: '' });
    try {
      const res = await api.delete(`/api/flight-plan/${selected.flightPlanUid}`);
      if (res.data.success !== false) {
        setFlightPlans(prev => prev.filter(fp => fp.flightPlanUid !== selected.flightPlanUid));
        setSelected(null);
        setMode('view');
        setConfirmDelete(false);
      } else {
        setMsg({ text: res.data.message || '삭제에 실패했습니다.', type: 'error' });
        setConfirmDelete(false);
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  }

  function fpMeta(fp) {
    const dep = findVp(fp.depVertiportUid);
    const arr = findVp(fp.arrVertiportUid);
    const ac  = findAc(fp.aircraftUid);
    return {
      route: `${dep?.vertiportId ?? fp.depVertiportUid} → ${arr?.vertiportId ?? fp.arrVertiportUid}`,
      acId:  ac?.aircraftId ?? `#${fp.aircraftUid}`,
    };
  }

  const LW = { width: 120 };

  return (
    <div className="acm">
      {/* ── 비행 계획 목록 ── */}
      <div className="acm-list" style={{ width: 300 }}>
        <div className="acm-list-hd">
          <span>비행 계획 목록 {!loading && `(${flightPlans.length}건)`}</span>
          <button className="acm-create-btn" onClick={openCreate}>＋ 등록</button>
        </div>
        {loading ? (
          <div className="acm-placeholder">불러오는 중...</div>
        ) : flightPlans.length === 0 ? (
          <div className="acm-placeholder">등록된 비행 계획이 없습니다.</div>
        ) : (
          <ul className="acm-items">
            {flightPlans.map(fp => {
              const { route, acId } = fpMeta(fp);
              return (
                <li key={fp.flightPlanUid}>
                  <button
                    className={`acm-item${selected?.flightPlanUid === fp.flightPlanUid && mode === 'view' ? ' active' : ''}`}
                    onClick={() => selectPlan(fp)}
                  >
                    <div className="acm-item-top">
                      <span className="acm-item-name" style={{ fontSize: 11, fontFamily: 'var(--mono)' }}>
                        {route}
                      </span>
                      <span className={`acm-badge ${STATUS_CSS[fp.status] ?? ''}`}>
                        {STATUS_LABELS[fp.status] ?? fp.status}
                      </span>
                    </div>
                    <div className="acm-item-id">
                      {acId} · {fp.scheduledDepTime ? fp.scheduledDepTime.replace('T', ' ').slice(0, 16) : '-'}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── 상세 / 수정 / 생성 ── */}
      <div className="acm-detail">
        {mode === 'create' ? (
          <div className="acm-form" style={{ maxWidth: 500 }}>
            <div className="acm-form-hd">비행 계획 등록</div>
            <div className="acm-section-label">필수 정보</div>

            <div className="acm-row">
              <label style={LW}>비행계획 ID *</label>
              <input type="text" value={createForm.flightPlanId}
                onChange={e => setC('flightPlanId', e.target.value)}
                className="acm-input" autoFocus placeholder="예: FP-20260620-001" />
            </div>
            <div className="acm-row">
              <label style={LW}>기체 *</label>
              <select value={createForm.aircraftUid} onChange={e => setC('aircraftUid', e.target.value)} className="acm-input">
                <option value="">-- 선택 --</option>
                {aircrafts.map(ac => (
                  <option key={ac.aircraftUid} value={ac.aircraftUid}>{ac.aircraftId} ({ac.modelName})</option>
                ))}
              </select>
            </div>
            <div className="acm-row">
              <label style={LW}>출발 버티포트 *</label>
              <select value={createForm.depVertiportUid} onChange={e => setC('depVertiportUid', e.target.value)} className="acm-input">
                <option value="">-- 선택 --</option>
                {vertiports.map(vp => (
                  <option key={vp.vertiportUid} value={vp.vertiportUid}>{vp.vertiportId} - {vp.name}</option>
                ))}
              </select>
            </div>
            <div className="acm-row">
              <label style={LW}>도착 버티포트 *</label>
              <select value={createForm.arrVertiportUid} onChange={e => setC('arrVertiportUid', e.target.value)} className="acm-input">
                <option value="">-- 선택 --</option>
                {vertiports.map(vp => (
                  <option key={vp.vertiportUid} value={vp.vertiportUid}>{vp.vertiportId} - {vp.name}</option>
                ))}
              </select>
            </div>
            <div className="acm-row">
              <label style={LW}>항로 *</label>
              <select value={createForm.airwayUid} onChange={e => setC('airwayUid', e.target.value)} className="acm-input">
                <option value="">-- 선택 --</option>
                {airways.map(aw => (
                  <option key={aw.airwayUid} value={aw.airwayUid}>
                    {aw.name}{aw.groupName ? ` [${aw.groupName}]` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="acm-row">
              <label style={LW}>순항 고도(m)</label>
              <input type="number" value={createForm.cruiseAlt}
                onChange={e => setC('cruiseAlt', e.target.value)} className="acm-input" min={0} />
            </div>
            <div className="acm-row">
              <label style={LW}>계획 출발 *</label>
              <input type="datetime-local" value={createForm.scheduledDepTime}
                onChange={e => setC('scheduledDepTime', e.target.value)} className="acm-input" />
            </div>
            <div className="acm-row">
              <label style={LW}>계획 도착 *</label>
              <input type="datetime-local" value={createForm.scheduledArrTime}
                onChange={e => setC('scheduledArrTime', e.target.value)} className="acm-input" />
            </div>

            {msg.text && <p className={`acm-msg acm-msg-${msg.type}`}>{msg.text}</p>}
            <div className="acm-btn-row">
              <button className="acm-save-btn" onClick={handleCreate} disabled={saving}>
                {saving ? '등록 중...' : '등록'}
              </button>
              <button className="acm-cancel-btn" onClick={() => { setMode('view'); setMsg({ text: '', type: '' }); }}>
                취소
              </button>
            </div>
          </div>
        ) : !selected ? (
          <div className="acm-placeholder">좌측 목록에서 비행 계획을 선택하세요.</div>
        ) : (
          <div className="acm-form" style={{ maxWidth: 500 }}>
            <div className="acm-form-hd fp-plan-hd">
              <span>{selected.flightPlanId}</span>
              {selected.status === 0 && (
                <button className="fp-approve-btn" onClick={handleApprove} disabled={saving}>
                  {saving ? '처리 중...' : '✓ 승인 (자동 경로지정)'}
                </button>
              )}
            </div>

            <div className="acm-section-label">식별 정보</div>
            <div className="acm-row">
              <label style={LW}>비행계획 ID</label>
              <input type="text" value={selected.flightPlanId} readOnly className="acm-input readonly" />
            </div>
            <div className="acm-row">
              <label style={LW}>등록일시</label>
              <input type="text" value={fmtDate(selected.regDate)} readOnly className="acm-input readonly" />
            </div>

            <div className="acm-section-label">운항 정보 (읽기 전용)</div>
            <div className="acm-row">
              <label style={LW}>기체</label>
              <input type="text" readOnly className="acm-input readonly"
                value={(() => { const ac = findAc(selected.aircraftUid); return ac ? `${ac.aircraftId} (${ac.modelName})` : String(selected.aircraftUid); })()} />
            </div>
            <div className="acm-row">
              <label style={LW}>출발 버티포트</label>
              <input type="text" readOnly className="acm-input readonly"
                value={(() => { const vp = findVp(selected.depVertiportUid); return vp ? `${vp.vertiportId} - ${vp.name}` : String(selected.depVertiportUid); })()} />
            </div>
            <div className="acm-row">
              <label style={LW}>도착 버티포트</label>
              <input type="text" readOnly className="acm-input readonly"
                value={(() => { const vp = findVp(selected.arrVertiportUid); return vp ? `${vp.vertiportId} - ${vp.name}` : String(selected.arrVertiportUid); })()} />
            </div>

            <div className="acm-section-label">수정</div>
            <div className="acm-row">
              <label style={LW}>항로</label>
              <select value={editForm.airwayUid} onChange={e => setE('airwayUid', e.target.value)} className="acm-input">
                <option value="">-- 없음 --</option>
                {airways.map(aw => (
                  <option key={aw.airwayUid} value={aw.airwayUid}>
                    {aw.name}{aw.groupName ? ` [${aw.groupName}]` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="acm-row">
              <label style={LW}>순항 고도(m)</label>
              <input type="number" value={editForm.cruiseAlt}
                onChange={e => setE('cruiseAlt', e.target.value)} className="acm-input" min={0} />
            </div>
            <div className="acm-row">
              <label style={LW}>계획 출발</label>
              <input type="datetime-local" value={editForm.scheduledDepTime}
                onChange={e => setE('scheduledDepTime', e.target.value)} className="acm-input" />
            </div>
            <div className="acm-row">
              <label style={LW}>계획 도착</label>
              <input type="datetime-local" value={editForm.scheduledArrTime}
                onChange={e => setE('scheduledArrTime', e.target.value)} className="acm-input" />
            </div>
            <div className="acm-row">
              <label style={LW}>실제 출발</label>
              <input type="datetime-local" value={editForm.actualDepTime}
                onChange={e => setE('actualDepTime', e.target.value)} className="acm-input" />
            </div>
            <div className="acm-row">
              <label style={LW}>실제 도착</label>
              <input type="datetime-local" value={editForm.actualArrTime}
                onChange={e => setE('actualArrTime', e.target.value)} className="acm-input" />
            </div>
            <div className="acm-row">
              <label style={LW}>상태</label>
              <select value={editForm.status} onChange={e => setE('status', e.target.value)} className="acm-input">
                <option value={0}>대기</option>
                <option value={1}>승인</option>
                <option value={2}>운항중</option>
                <option value={3}>완료</option>
                <option value={4}>취소</option>
              </select>
            </div>

            {msg.text && <p className={`acm-msg acm-msg-${msg.type}`}>{msg.text}</p>}
            <div className="acm-btn-row">
              <button className="acm-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? '저장 중...' : '수정'}
              </button>
              {confirmDelete ? (
                <>
                  <span className="acm-del-confirm">정말 삭제하시겠습니까?</span>
                  <button className="acm-save-btn acm-del-ok" onClick={handleDelete} disabled={saving}>확인</button>
                  <button className="acm-cancel-btn" onClick={() => setConfirmDelete(false)}>취소</button>
                </>
              ) : (
                <button className="acm-cancel-btn acm-del-btn" onClick={() => setConfirmDelete(true)}>삭제</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
